import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import api from '../services/api';

/* ── Helpers ── */
const fmt2 = (n) => parseFloat(n || 0).toFixed(2);

const fmtFechaHora = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  const fecha = dt.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const hora  = dt.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit', hour12: true });
  return `${fecha}  ${hora}`;
};

const fmtFechaCorta = (d) => {
  if (!d) return '';
  const s = d instanceof Date ? d.toISOString().slice(0, 10) : String(d).slice(0, 10);
  const [y, m, dia] = s.split('-');
  return `${dia}/${m}/${y}`;
};

/* ── Número a letras (español peruano) ── */
const UNIDADES = ['', 'UN', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE',
  'DIEZ', 'ONCE', 'DOCE', 'TRECE', 'CATORCE', 'QUINCE', 'DIECISÉIS', 'DIECISIETE', 'DIECIOCHO', 'DIECINUEVE'];
const DECENAS  = ['', '', 'VEINTE', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];
const CENTENAS = ['', 'CIENTO', 'DOSCIENTOS', 'TRESCIENTOS', 'CUATROCIENTOS', 'QUINIENTOS',
  'SEISCIENTOS', 'SETECIENTOS', 'OCHOCIENTOS', 'NOVECIENTOS'];

function cientos(n) {
  if (n === 0) return '';
  if (n === 100) return 'CIEN';
  const c = Math.floor(n / 100);
  const resto = n % 100;
  const parteC = c ? CENTENAS[c] : '';
  let parteD = '';
  if (resto < 20) {
    parteD = UNIDADES[resto];
  } else {
    const d = Math.floor(resto / 10);
    const u = resto % 10;
    parteD = DECENAS[d] + (u ? ' Y ' + UNIDADES[u] : '');
  }
  return [parteC, parteD].filter(Boolean).join(' ');
}

function enteroALetras(n) {
  if (n === 0) return 'CERO';
  if (n < 0) return 'MENOS ' + enteroALetras(-n);
  const partes = [];
  const millones = Math.floor(n / 1_000_000);
  const miles    = Math.floor((n % 1_000_000) / 1000);
  const resto    = n % 1000;
  if (millones) partes.push(millones === 1 ? 'UN MILLÓN' : cientos(millones) + ' MILLONES');
  if (miles)    partes.push(miles === 1 ? 'MIL' : cientos(miles) + ' MIL');
  if (resto)    partes.push(cientos(resto));
  return partes.join(' ');
}

function numToWords(amount) {
  const abs      = Math.abs(parseFloat(amount) || 0);
  const entero   = Math.floor(abs);
  const centavos = Math.round((abs - entero) * 100);
  return `${enteroALetras(entero)} CON ${String(centavos).padStart(2, '0')}/100 SOLES`;
}

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const logoUrl = (empresa) => empresa?.logo ? `${API}${empresa.logo}` : null;

const docTipo = (venta) => (venta?.documento_pago_nombre || 'BOLETA DE VENTA').toUpperCase();
const formaPagoTexto = (venta) =>
  venta?.condicion_pago !== 'CREDITO' && venta?.forma_pago_nombre
    ? `${venta.condicion_pago} — ${venta.forma_pago_nombre}`
    : venta?.condicion_pago;

/* ══════════════════════════════════════════════════════
   FORMATO A4  —  basado en papel_a4.pdf
══════════════════════════════════════════════════════ */
function ReciboA4({ venta, empresa }) {
  const descuento   = parseFloat(venta.descuento || 0);
  const igv         = parseFloat(venta.igv || 0);
  const total       = parseFloat(venta.total || 0);
  const subtotalB   = parseFloat(venta.subtotal || 0) + descuento; // antes de descuento
  const subtotalN   = parseFloat(venta.subtotal || 0);              // neto
  const conIgv      = igv > 0;
  const opGravada   = conIgv ? total - igv : 0;
  const opInafecta  = conIgv ? 0 : subtotalN;

  return (
    <div className="recibo a4">
      {/* ══ ENCABEZADO 2 COLUMNAS ══ */}
      <div className="a4-header">
        {/* Columna izquierda: logo + empresa */}
        <div className="a4-hdr-left">
          {logoUrl(empresa) && (
            <img src={logoUrl(empresa)} alt="logo" className="a4-logo" />
          )}
          <div className="a4-empresa-ruc">RUC: {empresa?.ruc || ''}</div>
          {empresa?.direccion && <div className="a4-empresa-dir">{empresa.direccion}</div>}
          {empresa?.correo    && <div className="a4-empresa-tel">{empresa.correo}</div>}
        </div>
        {/* Columna derecha: tipo doc + número */}
        <div className="a4-hdr-right">
          <div className="a4-doc-box">
            <div className="a4-doc-tipo">{docTipo(venta)}</div>
            <div className="a4-doc-num">{venta.numero}</div>
          </div>
        </div>
      </div>

      {/* ══ DATOS GENERALES ══ */}
      <div className="a4-info-block">
        <div className="a4-info-row">
          <span className="a4-info-key">FECHA</span>
          <span className="a4-info-val">{fmtFechaHora(venta.creado_en)}</span>
        </div>
        <div className="a4-info-row a4-info-row-senor">
          <span className="a4-info-key">SEÑOR(ES)</span>
          <span className="a4-info-val bold">{venta.cliente_nombre || 'CLIENTES VARIOS'}</span>
        </div>
        {venta.cliente_documento && (
          <div className="a4-info-row">
            <span className="a4-info-key">DNI / RUC</span>
            <span className="a4-info-val">{venta.cliente_documento}</span>
          </div>
        )}
        <div className="a4-info-row-2">
          <div>
            <span className="a4-info-key">MONEDA</span>
            <span className="a4-info-val">SOLES</span>
          </div>
          <div>
            <span className="a4-info-key">CAJERO</span>
            <span className="a4-info-val">{venta.vendedor_nombre || venta.caja_nombre || '—'}</span>
          </div>
          <div>
            <span className="a4-info-key">FORMA DE PAGO</span>
            <span className="a4-info-val">{formaPagoTexto(venta)}</span>
          </div>
        </div>
        {venta.condicion_pago !== 'CONTADO' && venta.fecha_vencimiento && (
          <div className="a4-info-row">
            <span className="a4-info-key">FECHA VENCIMIENTO</span>
            <span className="a4-info-val bold">{fmtFechaCorta(venta.fecha_vencimiento)}</span>
          </div>
        )}
        {venta.observacion && (
          <div className="a4-info-row">
            <span className="a4-info-key">OBSERVACIÓN</span>
            <span className="a4-info-val">{venta.observacion}</span>
          </div>
        )}
      </div>

      {/* ══ TABLA PRODUCTOS ══ */}
      <table className="a4-tabla">
        <thead>
          <tr>
            <th className="a4-th ctr" style={{ width: 46 }}>Cant</th>
            <th className="a4-th ctr" style={{ width: 40 }}>Und</th>
            <th className="a4-th">Descripción</th>
            <th className="a4-th num" style={{ width: 72 }}>P.U.</th>
            <th className="a4-th num" style={{ width: 80 }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {(venta.lineas || []).map((l, i) => {
            const subtL = parseFloat(l.subtotal || l.cantidad * l.precio_unitario);
            return (
              <tr key={l.id || i} className={i % 2 === 0 ? 'a4-tr-par' : ''}>
                <td className="a4-td ctr">{parseFloat(l.cantidad)}</td>
                <td className="a4-td ctr">{l.unidad_short || 'UND'}</td>
                <td className="a4-td">
                  <span className="a4-prod-name">{l.producto_nombre}</span>
                  {l.codigo && <span className="a4-prod-cod"> [{l.codigo}]</span>}
                </td>
                <td className="a4-td num">{fmt2(l.precio_unitario)}</td>
                <td className="a4-td num bold">{fmt2(subtL)}</td>
              </tr>
            );
          })}
          {/* Relleno mínimo */}
          {(venta.lineas || []).length < 4 &&
            Array.from({ length: 4 - (venta.lineas || []).length }).map((_, i) => (
              <tr key={`empty-${i}`}>
                <td className="a4-td">&nbsp;</td>
                <td className="a4-td"></td>
                <td className="a4-td"></td>
                <td className="a4-td"></td>
                <td className="a4-td"></td>
              </tr>
            ))
          }
        </tbody>
      </table>

      {/* ══ PIE: son + totales ══ */}
      <div className="a4-footer-block">
        {/* Izquierda: son */}
        <div className="a4-son-box">
          <div className="a4-son-label">SON:</div>
          <div className="a4-son-text">{numToWords(total)}</div>
        </div>

        {/* Derecha: totales */}
        <div className="a4-totales">
          {descuento > 0 && (
            <>
              <div className="a4-tot-row">
                <span>Subtotal</span>
                <span>{fmt2(subtotalB)}</span>
              </div>
              <div className="a4-tot-row">
                <span>Descuento</span>
                <span>- {fmt2(descuento)}</span>
              </div>
            </>
          )}
          {conIgv ? (
            <>
              <div className="a4-tot-row">
                <span>OP. Gravada</span>
                <span>{fmt2(opGravada)}</span>
              </div>
              <div className="a4-tot-row">
                <span>IGV 18%</span>
                <span>{fmt2(igv)}</span>
              </div>
            </>
          ) : (
            <div className="a4-tot-row">
              <span>OP. Inafecta</span>
              <span>{fmt2(opInafecta)}</span>
            </div>
          )}
          <div className="a4-tot-row total">
            <span>IMPORTE TOTAL</span>
            <span>{fmt2(total)}</span>
          </div>
          {venta.condicion_pago === 'MIXTO' && parseFloat(venta.monto_contado || 0) > 0 && (
            <>
              <div className="a4-tot-row">
                <span>ADELANTO AL CONTADO</span>
                <span>- {fmt2(venta.monto_contado)}</span>
              </div>
              <div className="a4-tot-row">
                <span>SALDO EN CRÉDITO</span>
                <span>{fmt2(total - parseFloat(venta.monto_contado))}</span>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="a4-firma-block">
        <div className="a4-firma-line">_______________________________</div>
        <div className="a4-firma-label">V° B°</div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   FORMATO 80mm  —  basado en papel_80.pdf
══════════════════════════════════════════════════════ */
function ReciboTicket({ venta, empresa, es60 }) {
  const descuento  = parseFloat(venta.descuento || 0);
  const igv        = parseFloat(venta.igv || 0);
  const total      = parseFloat(venta.total || 0);
  const subtotalB  = parseFloat(venta.subtotal || 0) + descuento;
  const subtotalN  = parseFloat(venta.subtotal || 0);
  const conIgv     = igv > 0;

  return (
    <div className={`recibo ticket${es60 ? ' t60' : ' t80'}`}>
      {/* ENCABEZADO */}
      <div className="tk-hdr">
        {logoUrl(empresa) && (
          <img src={logoUrl(empresa)} alt="logo" className="tk-logo" />
        )}
        {empresa?.ruc && <div className="tk-ruc">RUC: {empresa.ruc}</div>}
        {empresa?.direccion && <div className="tk-dir">{empresa.direccion}</div>}
      </div>

      <div className="tk-sep-solid" />

      <div className="tk-doc-tipo">{docTipo(venta)}</div>
      <div className="tk-doc-num">{venta.numero}</div>

      <div className="tk-sep-dot" />

      {/* INFO */}
      <div className="tk-info">
        <div className="tk-info-row">
          <span className="tk-key">FECHA</span>
          <span className="tk-val">{fmtFechaHora(venta.creado_en)}</span>
        </div>
        <div className="tk-info-row">
          <span className="tk-key">CLIENTE</span>
          <span className="tk-val">{venta.cliente_nombre || 'CLIENTES VARIOS'}</span>
        </div>
        {venta.cliente_documento && (
          <div className="tk-info-row">
            <span className="tk-key">DNI/RUC</span>
            <span className="tk-val">{venta.cliente_documento}</span>
          </div>
        )}
        <div className="tk-info-row">
          <span className="tk-key">FORMA PAGO</span>
          <span className="tk-val">{formaPagoTexto(venta)}</span>
        </div>
      </div>

      <div className="tk-sep-dot" />

      {/* PRODUCTOS */}
      <table className="tk-tabla">
        <thead>
          <tr>
            <th className="tk-th ctr">Cant</th>
            <th className="tk-th ctr">Und</th>
            <th className="tk-th">Descripción</th>
            <th className="tk-th num">P.U.</th>
            <th className="tk-th num">Total</th>
          </tr>
        </thead>
        <tbody>
          {(venta.lineas || []).map((l, i) => {
            const subtL = parseFloat(l.subtotal || l.cantidad * l.precio_unitario);
            return (
              <tr key={l.id || i}>
                <td className="tk-td ctr">{parseFloat(l.cantidad)}</td>
                <td className="tk-td ctr">{l.unidad_short || 'UND'}</td>
                <td className="tk-td">{l.producto_nombre}</td>
                <td className="tk-td num">{fmt2(l.precio_unitario)}</td>
                <td className="tk-td num bold">{fmt2(subtL)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="tk-sep-dot" />

      {/* SON */}
      <div className="tk-son">SON: {numToWords(total)}</div>

      <div className="tk-sep-dot" />

      {/* TOTALES */}
      <div className="tk-totales">
        {descuento > 0 && (
          <>
            <div className="tk-tot-row">
              <span>Subtotal</span><span>{fmt2(subtotalB)}</span>
            </div>
            <div className="tk-tot-row">
              <span>Descuento</span><span>- {fmt2(descuento)}</span>
            </div>
          </>
        )}
        {conIgv ? (
          <>
            <div className="tk-tot-row">
              <span>OP. Gravada</span><span>{fmt2(total - igv)}</span>
            </div>
            <div className="tk-tot-row">
              <span>IGV 18%</span><span>{fmt2(igv)}</span>
            </div>
          </>
        ) : (
          <div className="tk-tot-row">
            <span>OP. Inafecta</span><span>{fmt2(subtotalN)}</span>
          </div>
        )}
        <div className="tk-sep-dot" />
        <div className="tk-tot-row total">
          <span>IMPORTE TOTAL</span><span>{fmt2(total)}</span>
        </div>
        {venta.condicion_pago === 'MIXTO' && parseFloat(venta.monto_contado || 0) > 0 && (
          <>
            <div className="tk-tot-row">
              <span>ADELANTO CONTADO</span><span>- {fmt2(venta.monto_contado)}</span>
            </div>
            <div className="tk-tot-row">
              <span>SALDO CRÉDITO</span><span>{fmt2(total - parseFloat(venta.monto_contado))}</span>
            </div>
          </>
        )}
      </div>

      {venta.observacion && (
        <div className="tk-obs">Obs: {venta.observacion}</div>
      )}

      <div className="tk-sep-dot" />
      <div className="tk-footer">¡Gracias por su preferencia!</div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   ESTILOS GLOBALES
══════════════════════════════════════════════════════ */
const CSS_GLOBAL = (fmt) => `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  /* margin 0 en @page: evita que el navegador imprima su propia cabecera/pie
     (URL, fecha, nro de página); los márgenes van como padding del .recibo */
  @page {
    size: ${fmt === 'a4' ? 'A4' : fmt === '80' ? '80mm auto' : '60mm auto'};
    margin: 0;
  }

  /* ── A4 ── */
  body.fmt-a4 {
    font-family: Arial, Helvetica, sans-serif;
    font-size: 9.5pt;
    color: #111;
    background: #fff;
  }

  /* Header 2 columnas */
  .a4 .a4-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 10px;
    gap: 16px;
  }
  .a4 .a4-hdr-left {
    flex: 1;
  }
  .a4 .a4-logo {
    max-width: 260px;
    max-height: 160px;
    margin-bottom: 6px;
    display: block;
  }
  .a4 .a4-empresa-ruc {
    font-size: 10pt;
    font-weight: bold;
    margin-top: 2px;
  }
  .a4 .a4-empresa-dir,
  .a4 .a4-empresa-tel {
    font-size: 8pt;
    color: #444;
    margin-top: 2px;
  }

  /* Caja documento (derecha) */
  .a4 .a4-hdr-right {
    flex-shrink: 0;
    width: 180px;
  }
  .a4 .a4-doc-box {
    border: 2px solid #222;
    text-align: center;
    padding: 8px 12px;
  }
  .a4 .a4-doc-tipo {
    font-size: 12pt;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .a4 .a4-doc-num {
    font-size: 11pt;
    font-weight: bold;
    margin-top: 4px;
    letter-spacing: 1px;
    color: #333;
    border-top: 1px solid #aaa;
    padding-top: 4px;
    margin-top: 6px;
  }

  /* Info block */
  .a4 .a4-info-block {
    border-top: 1.5px solid #222;
    border-bottom: 1.5px solid #222;
    padding: 6px 0;
    margin-bottom: 10px;
  }
  .a4 .a4-info-row {
    display: flex;
    gap: 12px;
    border-bottom: 1px dotted #bbb;
    padding: 3px 2px;
    align-items: baseline;
  }
  .a4 .a4-info-row-2 {
    display: flex;
    gap: 20px;
    border-bottom: 1px dotted #bbb;
    padding: 3px 2px;
  }
  .a4 .a4-info-row-senor .a4-info-val {
    font-size: 8pt;
  }
  .a4 .a4-info-row-2 > div {
    display: flex;
    gap: 8px;
    align-items: baseline;
  }
  .a4 .a4-info-key {
    font-weight: bold;
    font-size: 8.5pt;
    min-width: 110px;
    flex-shrink: 0;
  }
  .a4 .a4-info-val {
    font-size: 9pt;
    color: #222;
  }
  .a4 .a4-info-val.bold { font-weight: bold; }

  /* Tabla productos */
  .a4 .a4-tabla {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 10px;
    font-size: 9pt;
  }
  .a4 .a4-th {
    background: #222;
    color: #fff;
    padding: 5px 6px;
    text-align: left;
    font-size: 8.5pt;
    font-weight: bold;
  }
  .a4 .a4-td {
    padding: 4px 6px;
    border-bottom: 1px dotted #ccc;
    vertical-align: top;
    font-size: 9pt;
  }
  .a4 .a4-tr-par td { background: #f9f9f9; }
  .a4 .a4-prod-name { }
  .a4 .a4-prod-cod { font-size: 7.5pt; color: #666; }

  /* Footer block: son + totales */
  .a4 .a4-footer-block {
    display: flex;
    gap: 16px;
    align-items: flex-start;
    border-top: 1.5px solid #222;
    padding-top: 8px;
    margin-bottom: 14px;
  }
  .a4 .a4-son-box {
    flex: 1;
    border: 1px dotted #999;
    padding: 6px 8px;
    font-size: 8.5pt;
  }
  .a4 .a4-son-label {
    font-weight: bold;
    font-size: 8pt;
    margin-bottom: 3px;
    text-transform: uppercase;
  }
  .a4 .a4-son-text {
    font-style: italic;
    text-transform: uppercase;
    word-break: break-word;
  }
  .a4 .a4-totales {
    min-width: 200px;
    font-size: 9pt;
  }
  .a4 .a4-tot-row {
    display: flex;
    justify-content: space-between;
    padding: 2px 4px;
    border-bottom: 1px dotted #ccc;
  }
  .a4 .a4-tot-row.total {
    font-weight: bold;
    font-size: 8.5pt;
    border-top: 2px solid #222;
    border-bottom: 2px solid #222;
    padding: 4px;
    margin-top: 2px;
  }

  .a4 .a4-firma-block {
    text-align: center;
    margin-top: 30px;
    font-size: 8pt;
    color: #555;
  }
  .a4 .a4-firma-label { margin-top: 4px; font-weight: bold; }

  /* ── TICKET (80mm y 60mm) ── */
  body.fmt-80 {
    font-family: 'Courier New', Courier, monospace;
    font-size: 9pt;
    color: #000;
    background: #fff;
    width: 76mm;
    max-width: 76mm;
  }
  body.fmt-60 {
    font-family: 'Courier New', Courier, monospace;
    font-size: 7.5pt;
    color: #000;
    background: #fff;
    width: 56mm;
    max-width: 56mm;
  }

  .ticket { padding: 2mm; }

  /* Header ticket */
  .tk-hdr { text-align: center; margin-bottom: 4px; }
  .tk-logo { max-width: 120px; max-height: 100px; margin-bottom: 3px; }
  .t80 .tk-ruc { font-size: 10.5pt; font-weight: bold; margin-top: 2px; }
  .t60 .tk-ruc { font-size: 8.5pt; font-weight: bold; margin-top: 2px; }
  .tk-dir { font-size: 7.5pt; font-style: italic; color: #333; margin-top: 1px; }

  /* Separadores */
  .tk-sep-solid { border: none; border-top: 2px solid #000; margin: 4px 0; }
  .tk-sep-dot   { border: none; border-top: 1px dashed #555; margin: 3px 0; }

  /* Título doc */
  .t80 .tk-doc-tipo { text-align: center; font-size: 10.5pt; font-weight: bold; text-transform: uppercase; }
  .t60 .tk-doc-tipo { text-align: center; font-size: 8.5pt; font-weight: bold; text-transform: uppercase; }
  .t80 .tk-doc-num  { text-align: center; font-size: 9pt; margin-bottom: 2px; letter-spacing: 0.5px; }
  .t60 .tk-doc-num  { text-align: center; font-size: 7.5pt; margin-bottom: 2px; }

  /* Info */
  .tk-info { margin: 3px 0; }
  .tk-info-row { display: flex; gap: 4px; margin-bottom: 1px; }
  .t80 .tk-key  { font-size: 7.5pt; font-weight: bold; min-width: 68px; flex-shrink: 0; }
  .t60 .tk-key  { font-size: 6.5pt; font-weight: bold; min-width: 56px; flex-shrink: 0; }
  .t80 .tk-val  { font-size: 7.5pt; }
  .t60 .tk-val  { font-size: 6.5pt; }

  /* Tabla */
  .tk-tabla { width: 100%; border-collapse: collapse; margin: 2px 0; }
  .t80 .tk-th { font-size: 7pt; font-weight: bold; padding: 2px 2px; border-bottom: 1px solid #000; text-align: left; }
  .t60 .tk-th { font-size: 6pt; font-weight: bold; padding: 1px 1px; border-bottom: 1px solid #000; text-align: left; }
  .t80 .tk-td { font-size: 7.5pt; padding: 2px 2px; border-bottom: 1px dotted #aaa; vertical-align: top; }
  .t60 .tk-td { font-size: 6.5pt; padding: 1px 2px; border-bottom: 1px dotted #aaa; vertical-align: top; }

  /* SON */
  .t80 .tk-son { font-size: 7pt; font-style: italic; text-transform: uppercase; margin: 3px 0; word-break: break-word; }
  .t60 .tk-son { font-size: 6pt; font-style: italic; text-transform: uppercase; margin: 2px 0; word-break: break-word; }

  /* Totales */
  .tk-totales { margin: 2px 0; }
  .tk-tot-row { display: flex; justify-content: space-between; margin-bottom: 1px; }
  .t80 .tk-tot-row { font-size: 8pt; }
  .t60 .tk-tot-row { font-size: 6.5pt; }
  .tk-tot-row.total { font-weight: bold; }
  .t80 .tk-tot-row.total { font-size: 10.5pt; }
  .t60 .tk-tot-row.total { font-size: 8.5pt; }

  .t80 .tk-obs    { font-size: 7pt; font-style: italic; margin: 3px 0; }
  .t60 .tk-obs    { font-size: 6pt; font-style: italic; margin: 2px 0; }

  /* Footer */
  .tk-footer { text-align: center; font-weight: bold; text-transform: uppercase; margin-top: 4px; }
  .t80 .tk-footer { font-size: 8pt; }
  .t60 .tk-footer { font-size: 7pt; }

  /* Helpers */
  .ctr  { text-align: center; }
  .num  { text-align: right; }
  .bold { font-weight: bold; }

  /* Vista pantalla */
  @media screen {
    body { background: #d0d0d0 !important; }
    .recibo {
      background: #fff;
      margin: 20px auto;
      padding: 20px;
      box-shadow: 0 2px 12px rgba(0,0,0,.3);
    }
    body.fmt-a4 .recibo { max-width: 210mm; }
    body.fmt-80 .recibo { max-width: 80mm; }
    body.fmt-60 .recibo { max-width: 60mm; }
  }
  @media print {
    body { background: #fff !important; }
    .recibo { box-shadow: none !important; margin: 0 !important; }
    body.fmt-a4 .recibo { padding: 12mm 15mm !important; }
    body.fmt-80 .recibo,
    body.fmt-60 .recibo { padding: 3mm 2mm !important; }
  }
`;

/* ══════════════════════════════════════════════════════
   Página principal
══════════════════════════════════════════════════════ */
export default function VentaImprimirPage() {
  const { id }     = useParams();
  const [params]   = useSearchParams();
  const fmt        = params.get('fmt') || 'a4'; // 'a4' | '80' | '60'

  const [venta,  setVenta]  = useState(null);
  const [empresa, setEmpresa] = useState(null);
  const [error,  setError]  = useState('');
  const [listo,  setListo]  = useState(false);

  // Clase body según formato
  useEffect(() => {
    document.body.className = `fmt-${fmt}`;
    return () => { document.body.className = ''; };
  }, [fmt]);

  useEffect(() => {
    Promise.all([
      api.get(`/api/ventas/${id}`),
      api.get('/api/empresa'),
    ])
      .then(([rv, re]) => {
        const v = rv.data?.data || rv.data;
        if (v?.numero) document.title = v.numero;
        setVenta(v);
        setEmpresa(re.data?.empresa || re.data?.data || re.data);
        setListo(true);
      })
      .catch(() => setError('No se pudo cargar el comprobante.'));
  }, [id]);

  // Disparar print cuando datos estén listos
  useEffect(() => {
    if (!listo) return;
    const t = setTimeout(() => window.print(), 700);
    return () => clearTimeout(t);
  }, [listo]);

  const css = CSS_GLOBAL(fmt);

  if (error) return <><style>{css}</style><div style={{ padding: 20, color: 'red' }}>{error}</div></>;
  if (!listo) return <><style>{css}</style><div style={{ padding: 20 }}>Cargando comprobante...</div></>;

  return (
    <>
      <style>{css}</style>
      {fmt === 'a4'
        ? <ReciboA4 venta={venta} empresa={empresa} />
        : <ReciboTicket venta={venta} empresa={empresa} es60={fmt === '60'} />
      }
    </>
  );
}
